import React, { useState, useEffect } from 'react';
import { useTheme } from '@emotion/react';
import PropTypes, { InferProps } from 'prop-types';
import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';
import { useQuery } from '@apollo/client';
import CategoryFilter
  from '@fuww/library/src/ProductFilters/CategoryFilter';
import FilterMenu
  from '@fuww/library/src/ProductFilters/FilterMenu';
import SearchFilterComponent
  from '@fuww/library/src/ProductFilters/SearchFilter';
import { ViewResultsButton }
  from '@fuww/library/src/ProductFilters/FilterDrawer';
import FavoritesFilter
  from '@fuww/library/src/ProductFilters/FavoritesFilter';
import DoneAllIcon from '@fuww/library/src/Icons/DoneAll';
import FilterPanel, { CategoryFilterPlaceholder } from './FilterPanel';
import InView from '../InView';
import messages from '../../lib/messages.json';
import withQuery from '../../lib/withQuery';
import productCategoriesQuery
  from '../../lib/queries/products/filters/productCategories.graphql';
import productsCountQuery
  from '../../lib/queries/products/productsCountQuery.graphql';
import debouncedReplaceRoute from '../../lib/debouncedReplaceRoute';
import replaceRoute from '../../lib/replaceRoute';

const defaultParameters = {
  brandIds: null,
  sizes: null,
  colorIds: null,
  department: null,
  minPrice: null,
  maxPrice: null,
  seasons: null,
  printIds: null,
  materialIds: null,
  search: null,
  model: null,
};

const searchFilterPropertyTypes = {
  queryParams: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
};

type SearchFilterProperties = InferProps<typeof searchFilterPropertyTypes>;

const SearchFilter = ({
  queryParams,
}: SearchFilterProperties) => {
  const { route: currentRouteWithSlash } = useRouter();

  const { search, ...queryParametersWithoutSearch } = queryParams;
  const [currentSearch, setCurrentSearch] = useState(search ?? null);
  const currentRoute = currentRouteWithSlash.slice(1);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const eventValue = event.target.value || null;

    setCurrentSearch(eventValue);

    debouncedReplaceRoute(currentRoute, {
      ...queryParametersWithoutSearch,
      ...(eventValue ? { search: eventValue } : {}),
    });
  };

  return (
    <SearchFilterComponent
      label="Search"
      search={currentSearch}
      onChange={handleSearchChange}
      onClear={() => {
        setCurrentSearch(null);
        setTimeout(() => {
          debouncedReplaceRoute(
            currentRoute,
            {
              ...queryParams,
              search: null,
            },
          );
        }, 100);
      }}
    />
  );
};

SearchFilter.propTypes = searchFilterPropertyTypes;

const favoritesSelectorPropertyTypes = {
  queryParams: PropTypes.shape({
    favorites: PropTypes.string,
  }).isRequired,
  favoriteIds: PropTypes.shape({
    size: PropTypes.number.isRequired,
  }).isRequired,
};

type FavoritesSelectorProperties = InferProps<
  typeof favoritesSelectorPropertyTypes
>;

const FavoritesSelector = ({
  queryParams: queryParameters,
  favoriteIds,
}: FavoritesSelectorProperties) => {
  const { route: currentRouteWithSlash } = useRouter();

  const currentRoute = currentRouteWithSlash.slice(1);
  const { favorites, ...rest } = queryParameters;

  return favoriteIds.size > 0 ? (
    <FavoritesFilter
      title="Show Favorites Only"
      onClick={() => {
        replaceRoute(
          currentRoute,
          {
            ...rest,
            ...(favorites ? {} : { favorites: 'true' }),
          },
        );
      }}
      selected={Boolean(favorites)}
    />
  ) : null;
};

FavoritesSelector.propTypes = favoritesSelectorPropertyTypes;

const sortSelectorPropertyTypes = {
  queryParams: PropTypes.shape({ sort: PropTypes.string }).isRequired,
};

type SortSelectorProperties = InferProps<typeof sortSelectorPropertyTypes>;

const SortSelector = ({
  queryParams: queryParameters,
}: SortSelectorProperties) => {
  const { route: currentRouteWithSlash } = useRouter();
  const intl = useIntl();

  const currentRoute = currentRouteWithSlash.slice(1);

  return (
    <CategoryFilter
      title={intl.formatMessage(messages.sorting)}
      categories={
            [
              {
                name: intl.formatMessage(messages.latest),
                handleInteraction: () => {
                  const { sort, ...rest } = queryParameters;
                  replaceRoute(
                    currentRoute,
                    rest,
                  );
                },
              },
              {
                name: intl.formatMessage(messages.alphabetical),
                handleInteraction: () => {
                  replaceRoute(
                    currentRoute,
                    {
                      ...queryParameters,
                      sort: 'ALPHABETICAL',
                    },
                  );
                },
              },
            ]
          }
      selectedCategory={
        queryParameters.sort === 'ALPHABETICAL'
          ? intl.formatMessage(messages.alphabetical)
          : intl.formatMessage(messages.latest)
      }
      loading={false}
      name={intl.formatMessage(messages.sorting)}
    />
  );
};

SortSelector.propTypes = sortSelectorPropertyTypes;

const mainCategoryFilterPropertyTypes = {
  data: PropTypes.shape({
    productMainCategories: PropTypes.arrayOf(
      PropTypes.string.isRequired,
    ).isRequired,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  queryParams: PropTypes.shape({
    category: PropTypes.string,
    department: PropTypes.string,
  }).isRequired,
  variables: PropTypes.shape({
    mainCategories: PropTypes.arrayOf(
      PropTypes.string,
    ).isRequired,
  }).isRequired,
};

type MainCategoryFilterProperties = InferProps<
  typeof mainCategoryFilterPropertyTypes
>;

const MainCategoryFilter = ({
  data,
  variables,
  loading,
  queryParams,
}: MainCategoryFilterProperties) => {
  const intl = useIntl();
  const { route: currentRouteWithSlash } = useRouter();

  const currentRoute = currentRouteWithSlash.slice(1);

  return (
    <CategoryFilter
      title={intl.formatMessage(messages.categories)}
      categories={
          [
            {
              name: 'All',
              handleInteraction: () => {
                const { category, ...rest } = queryParams;

                replaceRoute(
                  currentRoute,
                  rest,
                );
              },
            },
            ...data.productMainCategories.map((categoryName) => ({
              name: categoryName,
              handleInteraction: () => {
                replaceRoute(
                  currentRoute,
                  {
                    ...queryParams,
                    category: categoryName,
                  },
                );
              },
            })),
          ]
        }
      selectedCategory={
          variables.mainCategories.length === 0
            ? 'All'
            : variables.mainCategories[0]
        }
      loading={loading}
      name={intl.formatMessage(messages.categories)}
    />
  );
};

MainCategoryFilter.propTypes = mainCategoryFilterPropertyTypes;

const MainCategoryFilterWithQuery = withQuery(MainCategoryFilter);

const resultsButtonContentPropertyTypes = {
  variables: PropTypes.shape({
    locales: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    mainCategories: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  }).isRequired,
};

type ResultsButtonContentProperties = InferProps<
  typeof resultsButtonContentPropertyTypes
>;

const ResultsButtonContent = ({
  variables,
}: ResultsButtonContentProperties) => {
  const intl = useIntl();
  const { data } = useQuery(productsCountQuery, { variables });

  const resultsCount = data === undefined ? '' : data.productsCount;

  return (
    <>
      {intl.formatMessage(
        messages['products.seeResults'], { resultsCount },
      )}
    </>
  );
};

ResultsButtonContent.propTypes = resultsButtonContentPropertyTypes;

const resultsButtonPropertyTypes = {
  variables: PropTypes.shape({
    locales: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    mainCategories: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

type ResultsButtonProperties = InferProps<
  typeof resultsButtonPropertyTypes
>;

export const ResultsButton = ({
  variables,
  onClose,
}: ResultsButtonProperties) => {
  const theme = useTheme();
  return (
    <ViewResultsButton
      onClick={onClose}
      variant="contained"
      startIcon={<DoneAllIcon fill={theme.colors.white} />}
    >
      <InView>
        <ResultsButtonContent variables={variables} />
      </InView>
    </ViewResultsButton>
  );
};

ResultsButton.propTypes = resultsButtonPropertyTypes;

const productFilterPropertyTypes = {
  data: PropTypes.shape({
    productMainCategories: PropTypes.arrayOf(
      PropTypes.string,
    ),
  }),
  variables: PropTypes.shape({
    locales: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    mainCategories: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    marketId: PropTypes.number,
    standId: PropTypes.number,
    slug: PropTypes.string,
  }).isRequired,
  favoriteIds: PropTypes.shape({
    size: PropTypes.number.isRequired,
  }).isRequired,
};

type ProductFilterProperties = InferProps<
  typeof productFilterPropertyTypes
>;

const ProductFilters = ({
  variables,
  favoriteIds,
}: ProductFilterProperties) => {
  const intl = useIntl();
  const { query, route: currentRouteWithSlash } = useRouter();

  const {
    slug, category, marketId, standId,
  } = query;
  const resetParameters = {
    ...(category ? { category } : {}),
    ...(marketId ? { marketId } : {}),
    ...(standId ? { standId } : {}),
    ...(slug ? { slug } : {}),
  };

  const currentRoute = currentRouteWithSlash.slice(1);

  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [filtersActivated, setFiltersActivated] = useState(false);

  useEffect(() => {
    setFiltersActivated(
      Object.keys(query)
        .filter((queryParameterKey) => query[queryParameterKey])
        .some(
          (queryParameterKey) => Object.keys(defaultParameters)
            .includes(queryParameterKey),
        ),
    );
  }, [query]);

  return (
    <FilterMenu
      mainFilter={(
        <>
          <SearchFilter queryParams={query} />
          <FavoritesSelector queryParams={query} favoriteIds={favoriteIds} />
          <SortSelector queryParams={query} />
          <MainCategoryFilterWithQuery
            query={productCategoriesQuery}
            variables={variables}
            showLoaderWhenSetVariables={false}
            errorMessage="Error loading product categories"
            queryParams={query}
            loader={(
              <CategoryFilterPlaceholder
                title={intl.formatMessage(messages.categories)}
              />
            )}
          />
        </>
      )}
      filtersActivated={filtersActivated}
      filterDrawerOpen={filterDrawerOpen}
      openDrawer={() => { setFilterDrawerOpen(true); }}
      clearResults={() => {
        replaceRoute(currentRoute, resetParameters);
      }}
      filterMessage={`${intl.formatMessage(messages['products.filter'])}`}
      clearResultsMessage={
        `${intl.formatMessage(messages['products.clearResults'])}`
      }
      resultsButton={(
        <ResultsButton
          variables={variables}
          onClose={() => { setFilterDrawerOpen(false); }}
        />
      )}
    >
      <FilterPanel
        variables={variables}
        queryParams={query}
      />
    </FilterMenu>
  );
};

ProductFilters.propTypes = productFilterPropertyTypes;

export default ProductFilters;
